import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { agenteAfiliacionAPI } from '../../services/api';
import { Loader2, Bot, User as UserIcon, LifeBuoy, CheckCircle2 } from 'lucide-react';
import AsistenciaBadge from './AsistenciaBadge';

// P13D — Esqueleto del Agente 3: conversación DETERMINISTA (guion cerrado, sin IA ni LLM).
// Informa con contenido institucional editable (sin precios) y registra el prospecto de afiliación.
export const AgenteAfiliacion = ({ asesores, seccionActual, onCerrar, onGuardado }) => {
  const { toast } = useToast();
  const [guion, setGuion] = useState(null);
  const [info, setInfo] = useState(null);
  const [asesorId, setAsesorId] = useState('');
  const [sesion, setSesion] = useState(null);
  const [mensajes, setMensajes] = useState([]);
  const [valor, setValor] = useState('');
  const [cargando, setCargando] = useState(false);
  const [prospecto, setProspecto] = useState(null);
  const finRef = useRef(null);

  useEffect(() => {
    agenteAfiliacionAPI.guion().then(setGuion).catch(() => {});
    agenteAfiliacionAPI.info().then(setInfo).catch(() => {});
  }, []);
  useEffect(() => { finRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [mensajes]);

  const disponibles = seccionActual
    ? asesores.filter((a) => a.seccion_id === seccionActual) : asesores;
  const paso = sesion?.paso_actual || null;
  const error = (e) => toast({ title: 'Acción rechazada', description: e.message,
    variant: 'destructive' });

  const aplicar = (d, textoUsuario) => {
    setSesion(d);
    setValor('');
    setMensajes((m) => [
      ...m,
      ...(textoUsuario ? [{ de: 'humano', texto: textoUsuario }] : []),
      ...(d.mensaje_agente ? [{ de: 'agente', texto: d.mensaje_agente }] : []),
    ]);
  };

  const iniciar = async () => {
    setCargando(true);
    try {
      const d = await agenteAfiliacionAPI.crearSesion({ asesor_id: asesorId, canal: 'web_dev',
        consentimiento: true, origen: 'agente_afiliacion_dev' });
      setMensajes([{ de: 'agente', texto: guion?.presentacion || '' }]);
      aplicar(d, null);
    } catch (e) { error(e); } finally { setCargando(false); }
  };

  const responder = async (omitir) => {
    if (!paso) return;
    setCargando(true);
    try {
      const d = await agenteAfiliacionAPI.responder(sesion.sesion_id, {
        paso: paso.clave, valor: omitir ? null : valor,
        idempotency_key: `${sesion.sesion_id}:${paso.clave}:${sesion.interacciones}`,
      });
      aplicar(d, omitir ? '(omitido)' : String(valor));
    } catch (e) { error(e); } finally { setCargando(false); }
  };

  const confirmar = async () => {
    setCargando(true);
    try {
      const d = await agenteAfiliacionAPI.confirmar(sesion.sesion_id, {
        idempotency_key: `${sesion.sesion_id}:confirmar`,
      });
      setSesion(d.sesion);
      setProspecto(d.prospecto);
      setMensajes((m) => [...m, { de: 'agente',
        texto: `Prospecto registrado en ${d.prospecto?.seccion}. Un responsable humano dará seguimiento.` }]);
      toast({ title: 'Prospecto de afiliación registrado',
        description: `${d.prospecto?.nombre}` });
      onGuardado();
    } catch (e) { error(e); } finally { setCargando(false); }
  };

  const pedirHumano = async () => {
    setCargando(true);
    try {
      const d = await agenteAfiliacionAPI.handoff(sesion.sesion_id,
        { motivo: 'El prospecto solicita hablar con un responsable' });
      aplicar(d, 'Necesito apoyo humano');
    } catch (e) { error(e); } finally { setCargando(false); }
  };

  return (
    <Dialog open onOpenChange={onCerrar}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto"
                     data-testid="agente-afiliacion-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" /> Agente de prospectos de afiliación
            <Badge className="bg-slate-100 text-slate-700">esqueleto determinista</Badge>
            <AsistenciaBadge />
          </DialogTitle>
        </DialogHeader>

        {info && (
          <div className="border rounded-xl p-3 bg-indigo-50/60 text-sm space-y-2"
               data-testid="agente-afiliacion-info">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-800">Información de afiliación</span>
              <Badge className="bg-amber-100 text-amber-800 text-[10px]">
                editable por administración · sin precios
              </Badge>
            </div>
            <div className="grid sm:grid-cols-3 gap-2 text-xs text-gray-600">
              <div>
                <p className="font-medium text-gray-700">Beneficios</p>
                <ul className="list-disc pl-4">{info.beneficios.map((b, i) => <li key={i}>{b}</li>)}</ul>
              </div>
              <div>
                <p className="font-medium text-gray-700">Requisitos</p>
                <ul className="list-disc pl-4">{info.requisitos.map((r, i) => <li key={i}>{r}</li>)}</ul>
              </div>
              <div>
                <p className="font-medium text-gray-700">Proceso</p>
                <ul className="list-disc pl-4">{info.proceso.map((p, i) => <li key={i}>{p}</li>)}</ul>
              </div>
            </div>
            <p className="text-[11px] text-gray-500">{info.aviso}</p>
          </div>
        )}

        {!sesion && (
          <div className="space-y-3" data-testid="agente-afiliacion-inicio">
            <p className="text-sm text-gray-600">{guion?.presentacion}</p>
            <select value={asesorId} onChange={(e) => setAsesorId(e.target.value)}
                    className="border rounded-lg px-3 py-2 text-sm bg-white w-full"
                    data-testid="agente-afiliacion-asesor">
              <option value="">Asesor responsable del prospecto…</option>
              {disponibles.map((a) => (
                <option key={a.asesor_id} value={a.asesor_id}>{a.nombre}</option>
              ))}
            </select>
            <Button onClick={iniciar} disabled={!asesorId || cargando}
                    data-testid="agente-afiliacion-iniciar-btn">
              {cargando && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Iniciar conversación
            </Button>
          </div>
        )}

        {sesion && (
          <div className="space-y-4" data-testid="agente-afiliacion-conversacion">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Badge className="bg-indigo-100 text-indigo-800"
                     data-testid="agente-afiliacion-estado">{sesion.estado}</Badge>
              <span className="text-gray-500" data-testid="agente-afiliacion-progreso">
                {sesion.progreso.respondidos}/{sesion.progreso.total_pasos} datos ·
                {' '}{sesion.progreso.requeridos_pendientes.length} obligatorio(s) pendiente(s)
              </span>
              {sesion.handoff_humano && (
                <Badge className="bg-amber-100 text-amber-800"
                       data-testid="agente-afiliacion-handoff">apoyo humano solicitado</Badge>
              )}
            </div>

            <div className="border rounded-xl p-3 space-y-3 max-h-56 overflow-y-auto bg-gray-50">
              {mensajes.filter((m) => m.texto).map((m, i) => (
                <div key={i} className={`flex gap-2 text-sm ${m.de === 'humano'
                  ? 'justify-end text-right' : ''}`}>
                  {m.de === 'agente' && <Bot className="h-4 w-4 mt-1 text-indigo-600 shrink-0" />}
                  <span className={`rounded-2xl px-3 py-2 max-w-[80%] ${m.de === 'humano'
                    ? 'bg-indigo-600 text-white' : 'bg-white border'}`}>{m.texto}</span>
                  {m.de === 'humano' && <UserIcon className="h-4 w-4 mt-1 text-gray-400 shrink-0" />}
                </div>
              ))}
              <div ref={finRef} />
            </div>

            {paso && (
              <div className="flex flex-wrap items-center gap-2">
                {paso.opciones
                  ? (
                    <select value={valor} onChange={(e) => setValor(e.target.value)}
                            className="border rounded-lg px-3 py-2 text-sm bg-white flex-1"
                            data-testid="agente-afiliacion-valor">
                      <option value="">Elige una opción…</option>
                      {paso.opciones.map((o) => (
                        <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>
                      ))}
                    </select>
                  )
                  : (
                    <Input value={valor} className="flex-1"
                           type={['entero', 'decimal', 'moneda'].includes(paso.tipo)
                             ? 'number' : 'text'}
                           placeholder="Tu respuesta"
                           onChange={(e) => setValor(e.target.value)}
                           data-testid="agente-afiliacion-valor" />
                  )}
                <Button onClick={() => responder(false)} disabled={cargando || valor === ''}
                        data-testid="agente-afiliacion-responder-btn">
                  {cargando && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Responder
                </Button>
                {!paso.requerido && (
                  <Button variant="outline" onClick={() => responder(true)} disabled={cargando}
                          data-testid="agente-afiliacion-omitir-btn">Omitir</Button>
                )}
                <Button variant="ghost" onClick={pedirHumano} disabled={cargando}
                        data-testid="agente-afiliacion-handoff-btn">
                  <LifeBuoy className="h-4 w-4 mr-2" />Apoyo humano
                </Button>
              </div>
            )}

            {sesion.estado === 'lista_para_confirmar' && !prospecto && (
              <Button onClick={confirmar} disabled={cargando}
                      data-testid="agente-afiliacion-confirmar-btn">
                {cargando && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Registrar prospecto de afiliación
              </Button>
            )}

            {prospecto && (
              <div className="border rounded-xl p-3 bg-green-50 text-sm"
                   data-testid="agente-afiliacion-resultado">
                <p className="font-medium text-green-800 flex items-center gap-1">
                  <CheckCircle2 className="h-4 w-4" /> {prospecto.nombre}
                </p>
                <p className="text-gray-600">
                  Etapa {prospecto.etapa} · {prospecto.seccion}. El agente no aprueba ni cobra: un
                  responsable humano dará seguimiento.
                </p>
                <Button variant="outline" className="mt-2" onClick={onCerrar}
                        data-testid="agente-afiliacion-cerrar-btn">Cerrar</Button>
              </div>
            )}

            <p className="text-xs text-gray-500">
              Guion determinista: sin IA generativa, sin claves de modelos, sin WhatsApp y sin
              envíos externos. Deduplica por correo y teléfono; no informa cuotas ni aprueba
              afiliaciones.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AgenteAfiliacion;
